/**
 * Catálogo de emojis da bandeja.
 *
 * A lista é escrita à mão em vez de vir de um pacote pronto por dois motivos.
 * O primeiro é peso: as bibliotecas completas passam de um megabyte com dados
 * que quase ninguém usa, e isso entraria no carregamento inicial da conversa. O
 * segundo é a busca: os pacotes trazem palavras-chave em inglês, e aqui alguém
 * vai digitar "coracao", "risada" ou "polegar" e espera achar.
 *
 * Os termos de busca são escritos sem acento de propósito. A busca também tira
 * os acentos do que a pessoa digita, então "coração" e "coracao" chegam ao mesmo
 * resultado, e não é preciso repetir cada palavra nas duas formas.
 */

export interface CategoriaDeEmoji {
  id: string
  nome: string
  /** Emoji que representa a categoria na barra de atalhos. */
  icone: string
  emojis: { char: string; termos: string[] }[]
}

export const CATEGORIAS_DE_EMOJI: CategoriaDeEmoji[] = [
  {
    id: 'rostos',
    nome: 'Rostos e emoções',
    icone: '😀',
    emojis: [
      { char: '😀', termos: ['sorriso', 'feliz', 'alegre'] },
      { char: '😃', termos: ['sorriso', 'feliz', 'animado'] },
      { char: '😄', termos: ['sorriso', 'feliz', 'olhos'] },
      { char: '😁', termos: ['sorriso', 'dentes', 'radiante'] },
      { char: '😆', termos: ['risada', 'gargalhada', 'rindo'] },
      { char: '😅', termos: ['risada', 'suor', 'alivio', 'nervoso'] },
      { char: '🤣', termos: ['rolando', 'rindo', 'gargalhada', 'kkk'] },
      { char: '😂', termos: ['chorando', 'rindo', 'lagrima', 'kkk'] },
      { char: '🙂', termos: ['sorriso', 'leve', 'simples'] },
      { char: '🙃', termos: ['cabeca', 'baixo', 'ironia', 'sarcasmo'] },
      { char: '😉', termos: ['piscada', 'piscar', 'olho'] },
      { char: '😊', termos: ['sorriso', 'corado', 'timido', 'feliz'] },
      { char: '😇', termos: ['anjo', 'auréola', 'santo', 'inocente'] },
      { char: '🥰', termos: ['amor', 'coracoes', 'apaixonado'] },
      { char: '😍', termos: ['amor', 'olhos', 'coracao', 'apaixonado'] },
      { char: '🤩', termos: ['estrelas', 'uau', 'deslumbrado'] },
      { char: '😘', termos: ['beijo', 'jogando', 'amor'] },
      { char: '😗', termos: ['beijo', 'bico'] },
      { char: '😚', termos: ['beijo', 'olhos', 'fechados'] },
      { char: '😙', termos: ['beijo', 'sorriso'] },
      { char: '🥲', termos: ['lagrima', 'sorriso', 'emocionado'] },
      { char: '😋', termos: ['delicia', 'gostoso', 'lingua', 'saboroso'] },
      { char: '😛', termos: ['lingua', 'brincadeira'] },
      { char: '😜', termos: ['lingua', 'piscada', 'brincadeira'] },
      { char: '🤪', termos: ['maluco', 'doido', 'louco'] },
      { char: '😝', termos: ['lingua', 'olhos', 'fechados'] },
      { char: '🤑', termos: ['dinheiro', 'cifrao', 'rico'] },
      { char: '🤗', termos: ['abraco', 'acolher'] },
      { char: '🤭', termos: ['ops', 'mao', 'boca', 'risinho'] },
      { char: '🤫', termos: ['silencio', 'shh', 'segredo'] },
      { char: '🤔', termos: ['pensando', 'duvida', 'hmm'] },
      { char: '🤐', termos: ['boca', 'fechada', 'ziper', 'calado'] },
      { char: '🤨', termos: ['sobrancelha', 'desconfiado', 'duvida'] },
      { char: '😐', termos: ['neutro', 'sem', 'expressao'] },
      { char: '😑', termos: ['inexpressivo', 'sem', 'graca'] },
      { char: '😶', termos: ['sem', 'boca', 'mudo'] },
      { char: '😏', termos: ['malicioso', 'sorrisinho', 'safado'] },
      { char: '😒', termos: ['desanimado', 'chateado', 'tedio'] },
      { char: '🙄', termos: ['revirar', 'olhos', 'ah'] },
      { char: '😬', termos: ['careta', 'constrangido', 'nervoso'] },
      { char: '😮‍💨', termos: ['suspiro', 'alivio', 'cansaco'] },
      { char: '🤥', termos: ['mentira', 'nariz', 'pinoquio'] },
      { char: '😌', termos: ['aliviado', 'calmo', 'tranquilo'] },
      { char: '😔', termos: ['triste', 'pensativo', 'chateado'] },
      { char: '😪', termos: ['sono', 'sonolento', 'cansado'] },
      { char: '🤤', termos: ['babando', 'baba', 'desejo'] },
      { char: '😴', termos: ['dormindo', 'sono', 'zzz'] },
      { char: '😷', termos: ['mascara', 'doente', 'gripe'] },
      { char: '🤒', termos: ['febre', 'doente', 'termometro'] },
      { char: '🤕', termos: ['machucado', 'curativo', 'ferido'] },
      { char: '🤢', termos: ['enjoado', 'nojo', 'verde'] },
      { char: '🤮', termos: ['vomitando', 'nojo', 'passando', 'mal'] },
      { char: '🤧', termos: ['espirro', 'resfriado', 'gripe'] },
      { char: '🥵', termos: ['calor', 'quente', 'suando'] },
      { char: '🥶', termos: ['frio', 'congelando', 'gelado'] },
      { char: '🥴', termos: ['tonto', 'bebado', 'zonzo'] },
      { char: '😵', termos: ['tonto', 'atordoado'] },
      { char: '🤯', termos: ['explodindo', 'cabeca', 'chocado', 'mente'] },
      { char: '🤠', termos: ['cowboy', 'chapeu'] },
      { char: '🥳', termos: ['festa', 'comemorando', 'aniversario'] },
      { char: '😎', termos: ['oculos', 'legal', 'estiloso', 'sol'] },
      { char: '🤓', termos: ['nerd', 'oculos', 'estudioso'] },
      { char: '🧐', termos: ['monoculo', 'analisando', 'curioso'] },
      { char: '😕', termos: ['confuso', 'incerto'] },
      { char: '😟', termos: ['preocupado', 'aflito'] },
      { char: '🙁', termos: ['triste', 'chateado'] },
      { char: '😮', termos: ['surpreso', 'boca', 'aberta', 'uau'] },
      { char: '😯', termos: ['surpreso', 'espantado'] },
      { char: '😲', termos: ['chocado', 'espantado', 'surpresa'] },
      { char: '😳', termos: ['corado', 'envergonhado', 'surpreso'] },
      { char: '🥺', termos: ['suplicante', 'pidao', 'olhinhos', 'favor'] },
      { char: '😦', termos: ['assustado', 'boca', 'aberta'] },
      { char: '😧', termos: ['angustiado', 'aflito'] },
      { char: '😨', termos: ['medo', 'assustado'] },
      { char: '😰', termos: ['ansioso', 'suor', 'nervoso'] },
      { char: '😥', termos: ['decepcionado', 'aliviado', 'triste'] },
      { char: '😢', termos: ['chorando', 'lagrima', 'triste'] },
      { char: '😭', termos: ['chorando', 'muito', 'soluçando', 'triste'] },
      { char: '😱', termos: ['grito', 'medo', 'pavor', 'susto'] },
      { char: '😖', termos: ['confuso', 'frustrado'] },
      { char: '😣', termos: ['perseverando', 'esforco'] },
      { char: '😞', termos: ['decepcionado', 'triste'] },
      { char: '😓', termos: ['suor', 'derrota', 'cansado'] },
      { char: '😩', termos: ['cansado', 'exausto', 'chateado'] },
      { char: '😫', termos: ['cansado', 'exausto', 'nao', 'aguento'] },
      { char: '🥱', termos: ['bocejo', 'sono', 'tedio'] },
      { char: '😤', termos: ['bufando', 'irritado', 'triunfo'] },
      { char: '😡', termos: ['bravo', 'raiva', 'furioso', 'vermelho'] },
      { char: '😠', termos: ['bravo', 'irritado', 'raiva'] },
      { char: '🤬', termos: ['xingando', 'palavrao', 'raiva'] },
      { char: '😈', termos: ['diabo', 'travesso', 'maldade'] },
      { char: '👿', termos: ['diabo', 'bravo', 'raiva'] },
      { char: '💀', termos: ['caveira', 'morte', 'morto'] },
      { char: '☠️', termos: ['caveira', 'ossos', 'perigo', 'morte'] },
      { char: '💩', termos: ['coco', 'merda', 'ruim'] },
      { char: '🤡', termos: ['palhaco', 'circo'] },
      { char: '👻', termos: ['fantasma', 'assombracao', 'halloween'] },
      { char: '👽', termos: ['alienigena', 'et', 'espaco'] },
      { char: '🤖', termos: ['robo', 'bot', 'maquina'] },
      { char: '🎃', termos: ['abobora', 'halloween'] },
      { char: '😺', termos: ['gato', 'sorriso'] },
      { char: '😹', termos: ['gato', 'rindo', 'lagrima'] },
      { char: '😻', termos: ['gato', 'amor', 'coracao'] },
      { char: '🙈', termos: ['macaco', 'nao', 'vejo', 'vergonha'] },
      { char: '🙉', termos: ['macaco', 'nao', 'ouco'] },
      { char: '🙊', termos: ['macaco', 'nao', 'falo', 'segredo'] },
    ],
  },
  {
    id: 'gestos',
    nome: 'Gestos e pessoas',
    icone: '👍',
    emojis: [
      { char: '👍', termos: ['joinha', 'polegar', 'positivo', 'curti', 'ok'] },
      { char: '👎', termos: ['polegar', 'baixo', 'negativo', 'ruim'] },
      { char: '👌', termos: ['ok', 'certo', 'perfeito'] },
      { char: '🤌', termos: ['dedos', 'italiano', 'gesto'] },
      { char: '🤏', termos: ['pouco', 'pequeno', 'pitada'] },
      { char: '✌️', termos: ['paz', 'vitoria', 'dois'] },
      { char: '🤞', termos: ['dedos', 'cruzados', 'sorte', 'torcendo'] },
      { char: '🤟', termos: ['amor', 'rock', 'mao'] },
      { char: '🤘', termos: ['rock', 'chifre', 'metal'] },
      { char: '🤙', termos: ['liga', 'shaka', 'telefone'] },
      { char: '👈', termos: ['esquerda', 'apontando', 'dedo'] },
      { char: '👉', termos: ['direita', 'apontando', 'dedo'] },
      { char: '👆', termos: ['cima', 'apontando', 'dedo'] },
      { char: '👇', termos: ['baixo', 'apontando', 'dedo'] },
      { char: '☝️', termos: ['cima', 'atencao', 'um'] },
      { char: '✋', termos: ['mao', 'parar', 'alto'] },
      { char: '🤚', termos: ['mao', 'levantada', 'costas'] },
      { char: '🖐️', termos: ['mao', 'dedos', 'abertos'] },
      { char: '🖖', termos: ['vulcano', 'spock', 'saudacao'] },
      { char: '👋', termos: ['tchau', 'oi', 'aceno', 'ola'] },
      { char: '🤝', termos: ['aperto', 'maos', 'acordo', 'negocio'] },
      { char: '🙏', termos: ['obrigado', 'por', 'favor', 'reza', 'agradecido'] },
      { char: '✍️', termos: ['escrevendo', 'mao', 'caneta'] },
      { char: '👏', termos: ['palmas', 'aplauso', 'parabens'] },
      { char: '🙌', termos: ['maos', 'levantadas', 'comemorando', 'aleluia'] },
      { char: '👐', termos: ['maos', 'abertas', 'abraco'] },
      { char: '🤲', termos: ['maos', 'juntas', 'oferecendo'] },
      { char: '💪', termos: ['forca', 'musculo', 'biceps', 'forte'] },
      { char: '🦾', termos: ['braco', 'mecanico', 'protese'] },
      { char: '🖕', termos: ['dedo', 'meio', 'ofensa'] },
      { char: '✊', termos: ['punho', 'forca', 'luta'] },
      { char: '👊', termos: ['soco', 'punho', 'toca', 'aqui'] },
      { char: '🫰', termos: ['dedos', 'coracao', 'dinheiro'] },
      { char: '🫶', termos: ['coracao', 'maos', 'amor'] },
      { char: '👀', termos: ['olhos', 'olhando', 'vendo', 'atencao'] },
      { char: '👁️', termos: ['olho', 'vendo'] },
      { char: '🧠', termos: ['cerebro', 'mente', 'inteligencia'] },
      { char: '🦷', termos: ['dente'] },
      { char: '👶', termos: ['bebe', 'crianca'] },
      { char: '🧒', termos: ['crianca'] },
      { char: '👦', termos: ['menino', 'garoto'] },
      { char: '👧', termos: ['menina', 'garota'] },
      { char: '🧑', termos: ['pessoa', 'adulto'] },
      { char: '👨', termos: ['homem'] },
      { char: '👩', termos: ['mulher'] },
      { char: '🧔', termos: ['barba', 'barbudo'] },
      { char: '👴', termos: ['idoso', 'velho', 'vo'] },
      { char: '👵', termos: ['idosa', 'velha', 'vo'] },
      { char: '👮', termos: ['policial', 'policia'] },
      { char: '👷', termos: ['obra', 'construcao', 'capacete'] },
      { char: '💂', termos: ['guarda', 'soldado'] },
      { char: '🕵️', termos: ['detetive', 'investigador', 'espiao'] },
      { char: '👨‍💻', termos: ['programador', 'desenvolvedor', 'computador', 'trabalho'] },
      { char: '👩‍💻', termos: ['programadora', 'desenvolvedora', 'computador', 'trabalho'] },
      { char: '🧑‍🍳', termos: ['cozinheiro', 'chef', 'cozinha'] },
      { char: '🧑‍🚀', termos: ['astronauta', 'espaco'] },
      { char: '🦸', termos: ['heroi', 'super'] },
      { char: '🦹', termos: ['vilao', 'super'] },
      { char: '🧙', termos: ['mago', 'feiticeiro', 'magia'] },
      { char: '🧚', termos: ['fada', 'magia'] },
      { char: '🎅', termos: ['papai', 'noel', 'natal'] },
      { char: '🤶', termos: ['mama', 'noel', 'natal'] },
      { char: '💃', termos: ['dancando', 'danca', 'festa'] },
      { char: '🕺', termos: ['dancando', 'danca', 'festa'] },
      { char: '🧘', termos: ['meditacao', 'ioga', 'calma'] },
      { char: '🏃', termos: ['correndo', 'corrida', 'pressa'] },
      { char: '🚶', termos: ['andando', 'caminhando'] },
      { char: '🤷', termos: ['ombros', 'sei', 'la', 'duvida'] },
      { char: '🤦', termos: ['facepalm', 'mao', 'rosto', 'vergonha'] },
      { char: '🙋', termos: ['levantando', 'mao', 'pergunta', 'eu'] },
      { char: '🙅', termos: ['nao', 'negando', 'proibido'] },
      { char: '🙆', termos: ['ok', 'certo', 'concordo'] },
      { char: '💁', termos: ['informacao', 'atendente', 'olha'] },
      { char: '👨‍👩‍👧', termos: ['familia', 'pais', 'filha'] },
      { char: '👫', termos: ['casal', 'maos', 'dadas'] },
    ],
  },
  {
    id: 'natureza',
    nome: 'Animais e natureza',
    icone: '🐶',
    emojis: [
      { char: '🐶', termos: ['cachorro', 'cao', 'dog'] },
      { char: '🐱', termos: ['gato', 'gata'] },
      { char: '🐭', termos: ['rato', 'camundongo'] },
      { char: '🐹', termos: ['hamster'] },
      { char: '🐰', termos: ['coelho'] },
      { char: '🦊', termos: ['raposa'] },
      { char: '🐻', termos: ['urso'] },
      { char: '🐼', termos: ['panda'] },
      { char: '🐨', termos: ['coala'] },
      { char: '🐯', termos: ['tigre'] },
      { char: '🦁', termos: ['leao'] },
      { char: '🐮', termos: ['vaca', 'boi'] },
      { char: '🐷', termos: ['porco'] },
      { char: '🐸', termos: ['sapo', 'ra'] },
      { char: '🐵', termos: ['macaco'] },
      { char: '🐔', termos: ['galinha', 'frango'] },
      { char: '🐧', termos: ['pinguim'] },
      { char: '🐦', termos: ['passaro', 'passarinho'] },
      { char: '🦆', termos: ['pato'] },
      { char: '🦅', termos: ['aguia'] },
      { char: '🦉', termos: ['coruja'] },
      { char: '🐺', termos: ['lobo'] },
      { char: '🐗', termos: ['javali'] },
      { char: '🐴', termos: ['cavalo'] },
      { char: '🦄', termos: ['unicornio'] },
      { char: '🐝', termos: ['abelha'] },
      { char: '🐛', termos: ['lagarta', 'bug', 'inseto'] },
      { char: '🦋', termos: ['borboleta'] },
      { char: '🐌', termos: ['caracol', 'lesma', 'lento'] },
      { char: '🐞', termos: ['joaninha'] },
      { char: '🐜', termos: ['formiga'] },
      { char: '🕷️', termos: ['aranha'] },
      { char: '🦂', termos: ['escorpiao'] },
      { char: '🐢', termos: ['tartaruga', 'lento'] },
      { char: '🐍', termos: ['cobra', 'serpente'] },
      { char: '🦎', termos: ['lagarto'] },
      { char: '🐙', termos: ['polvo'] },
      { char: '🦑', termos: ['lula'] },
      { char: '🦐', termos: ['camarao'] },
      { char: '🦀', termos: ['caranguejo'] },
      { char: '🐟', termos: ['peixe'] },
      { char: '🐠', termos: ['peixe', 'tropical'] },
      { char: '🐬', termos: ['golfinho'] },
      { char: '🐳', termos: ['baleia'] },
      { char: '🦈', termos: ['tubarao'] },
      { char: '🐊', termos: ['jacare', 'crocodilo'] },
      { char: '🐘', termos: ['elefante'] },
      { char: '🦒', termos: ['girafa'] },
      { char: '🦓', termos: ['zebra'] },
      { char: '🐄', termos: ['vaca'] },
      { char: '🐑', termos: ['ovelha'] },
      { char: '🐐', termos: ['cabra', 'bode'] },
      { char: '🐓', termos: ['galo'] },
      { char: '🦃', termos: ['peru'] },
      { char: '🕊️', termos: ['pomba', 'paz'] },
      { char: '🌵', termos: ['cacto'] },
      { char: '🌲', termos: ['arvore', 'pinheiro'] },
      { char: '🌳', termos: ['arvore'] },
      { char: '🌴', termos: ['palmeira', 'coqueiro'] },
      { char: '🌱', termos: ['muda', 'broto', 'planta'] },
      { char: '🌿', termos: ['erva', 'folha', 'planta'] },
      { char: '🍀', termos: ['trevo', 'sorte', 'quatro'] },
      { char: '🍁', termos: ['folha', 'outono', 'bordo'] },
      { char: '🍂', termos: ['folhas', 'outono'] },
      { char: '🌺', termos: ['flor', 'hibisco'] },
      { char: '🌸', termos: ['flor', 'cerejeira'] },
      { char: '🌼', termos: ['flor', 'margarida'] },
      { char: '🌻', termos: ['girassol', 'flor'] },
      { char: '🌹', termos: ['rosa', 'flor', 'amor'] },
      { char: '💐', termos: ['buque', 'flores'] },
      { char: '🌞', termos: ['sol', 'rosto'] },
      { char: '🌝', termos: ['lua', 'cheia', 'rosto'] },
      { char: '🌚', termos: ['lua', 'nova', 'rosto'] },
      { char: '⭐', termos: ['estrela'] },
      { char: '🌟', termos: ['estrela', 'brilhando'] },
      { char: '✨', termos: ['brilho', 'faisca', 'magia', 'novo'] },
      { char: '⚡', termos: ['raio', 'energia', 'rapido'] },
      { char: '🔥', termos: ['fogo', 'chama', 'top', 'quente'] },
      { char: '💥', termos: ['explosao', 'boom'] },
      { char: '☀️', termos: ['sol', 'ensolarado'] },
      { char: '⛅', termos: ['nuvem', 'sol', 'parcial'] },
      { char: '☁️', termos: ['nuvem', 'nublado'] },
      { char: '🌧️', termos: ['chuva', 'chovendo'] },
      { char: '⛈️', termos: ['tempestade', 'raio', 'chuva'] },
      { char: '❄️', termos: ['neve', 'floco', 'frio'] },
      { char: '☃️', termos: ['boneco', 'neve'] },
      { char: '🌈', termos: ['arco', 'iris', 'colorido'] },
      { char: '💧', termos: ['gota', 'agua'] },
      { char: '🌊', termos: ['onda', 'mar', 'agua'] },
      { char: '🌍', termos: ['terra', 'mundo', 'planeta', 'globo'] },
      { char: '🌙', termos: ['lua', 'noite'] },
    ],
  },
  {
    id: 'comida',
    nome: 'Comida e bebida',
    icone: '🍕',
    emojis: [
      { char: '🍏', termos: ['maca', 'verde', 'fruta'] },
      { char: '🍎', termos: ['maca', 'vermelha', 'fruta'] },
      { char: '🍐', termos: ['pera', 'fruta'] },
      { char: '🍊', termos: ['laranja', 'fruta'] },
      { char: '🍋', termos: ['limao', 'fruta'] },
      { char: '🍌', termos: ['banana', 'fruta'] },
      { char: '🍉', termos: ['melancia', 'fruta'] },
      { char: '🍇', termos: ['uva', 'fruta'] },
      { char: '🍓', termos: ['morango', 'fruta'] },
      { char: '🫐', termos: ['mirtilo', 'fruta'] },
      { char: '🍈', termos: ['melao', 'fruta'] },
      { char: '🍒', termos: ['cereja', 'fruta'] },
      { char: '🍑', termos: ['pessego', 'fruta', 'bumbum'] },
      { char: '🥭', termos: ['manga', 'fruta'] },
      { char: '🍍', termos: ['abacaxi', 'fruta'] },
      { char: '🥥', termos: ['coco', 'fruta'] },
      { char: '🥝', termos: ['kiwi', 'fruta'] },
      { char: '🍅', termos: ['tomate'] },
      { char: '🥑', termos: ['abacate'] },
      { char: '🥦', termos: ['brocolis'] },
      { char: '🥕', termos: ['cenoura'] },
      { char: '🌽', termos: ['milho'] },
      { char: '🌶️', termos: ['pimenta', 'picante'] },
      { char: '🥔', termos: ['batata'] },
      { char: '🍞', termos: ['pao'] },
      { char: '🥐', termos: ['croissant'] },
      { char: '🥖', termos: ['baguete', 'pao'] },
      { char: '🧀', termos: ['queijo'] },
      { char: '🥚', termos: ['ovo'] },
      { char: '🍳', termos: ['ovo', 'frito', 'cozinhar'] },
      { char: '🥓', termos: ['bacon'] },
      { char: '🥩', termos: ['carne', 'bife'] },
      { char: '🍗', termos: ['frango', 'coxa'] },
      { char: '🍖', termos: ['carne', 'osso'] },
      { char: '🌭', termos: ['cachorro', 'quente', 'hotdog'] },
      { char: '🍔', termos: ['hamburguer', 'lanche'] },
      { char: '🍟', termos: ['batata', 'frita'] },
      { char: '🍕', termos: ['pizza'] },
      { char: '🥪', termos: ['sanduiche', 'lanche'] },
      { char: '🌮', termos: ['taco'] },
      { char: '🌯', termos: ['burrito'] },
      { char: '🥗', termos: ['salada'] },
      { char: '🍝', termos: ['macarrao', 'espaguete', 'massa'] },
      { char: '🍜', termos: ['lamen', 'sopa', 'macarrao'] },
      { char: '🍲', termos: ['panela', 'ensopado', 'comida'] },
      { char: '🍛', termos: ['curry', 'arroz'] },
      { char: '🍣', termos: ['sushi'] },
      { char: '🍱', termos: ['marmita', 'bento'] },
      { char: '🥟', termos: ['guioza', 'pastel'] },
      { char: '🍤', termos: ['camarao', 'frito'] },
      { char: '🍚', termos: ['arroz'] },
      { char: '🥘', termos: ['paella', 'panela'] },
      { char: '🍿', termos: ['pipoca', 'cinema', 'filme'] },
      { char: '🧂', termos: ['sal'] },
      { char: '🍦', termos: ['sorvete', 'casquinha'] },
      { char: '🍩', termos: ['rosquinha', 'donut'] },
      { char: '🍪', termos: ['biscoito', 'cookie'] },
      { char: '🎂', termos: ['bolo', 'aniversario', 'parabens'] },
      { char: '🍰', termos: ['bolo', 'fatia', 'torta'] },
      { char: '🧁', termos: ['cupcake', 'bolinho'] },
      { char: '🥧', termos: ['torta'] },
      { char: '🍫', termos: ['chocolate'] },
      { char: '🍬', termos: ['bala', 'doce'] },
      { char: '🍭', termos: ['pirulito', 'doce'] },
      { char: '🍯', termos: ['mel'] },
      { char: '🍼', termos: ['mamadeira', 'leite'] },
      { char: '🥛', termos: ['leite', 'copo'] },
      { char: '☕', termos: ['cafe', 'quente', 'xicara'] },
      { char: '🍵', termos: ['cha', 'verde'] },
      { char: '🧉', termos: ['mate', 'chimarrao'] },
      { char: '🥤', termos: ['refrigerante', 'copo', 'canudo'] },
      { char: '🧃', termos: ['suco', 'caixinha'] },
      { char: '🍺', termos: ['cerveja', 'chopp'] },
      { char: '🍻', termos: ['cerveja', 'brinde', 'comemorar'] },
      { char: '🍷', termos: ['vinho', 'taca'] },
      { char: '🥂', termos: ['brinde', 'champanhe', 'comemorar'] },
      { char: '🍸', termos: ['drink', 'coquetel', 'martini'] },
      { char: '🍹', termos: ['drink', 'tropical', 'coquetel'] },
      { char: '🥃', termos: ['whisky', 'dose'] },
      { char: '🍾', termos: ['champanhe', 'garrafa', 'comemorar'] },
    ],
  },
  {
    id: 'atividades',
    nome: 'Atividades',
    icone: '⚽',
    emojis: [
      { char: '⚽', termos: ['futebol', 'bola'] },
      { char: '🏀', termos: ['basquete', 'bola'] },
      { char: '🏈', termos: ['futebol', 'americano'] },
      { char: '⚾', termos: ['beisebol'] },
      { char: '🎾', termos: ['tenis'] },
      { char: '🏐', termos: ['volei'] },
      { char: '🏉', termos: ['rugby'] },
      { char: '🎱', termos: ['sinuca', 'bilhar', 'oito'] },
      { char: '🏓', termos: ['pingue', 'pongue', 'tenis', 'mesa'] },
      { char: '🏸', termos: ['badminton'] },
      { char: '🥅', termos: ['gol', 'trave'] },
      { char: '⛳', termos: ['golfe', 'buraco'] },
      { char: '🏹', termos: ['arco', 'flecha'] },
      { char: '🎣', termos: ['pesca', 'pescaria'] },
      { char: '🥊', termos: ['boxe', 'luva'] },
      { char: '🥋', termos: ['artes', 'marciais', 'judo', 'karate'] },
      { char: '⛸️', termos: ['patinacao', 'gelo'] },
      { char: '🎿', termos: ['esqui', 'neve'] },
      { char: '🏂', termos: ['snowboard', 'neve'] },
      { char: '🏄', termos: ['surfe', 'onda'] },
      { char: '🏊', termos: ['natacao', 'nadando'] },
      { char: '🚴', termos: ['bicicleta', 'ciclismo', 'pedalando'] },
      { char: '🏋️', termos: ['academia', 'peso', 'musculacao'] },
      { char: '🤸', termos: ['ginastica', 'cambalhota'] },
      { char: '🏆', termos: ['trofeu', 'campeao', 'vitoria'] },
      { char: '🥇', termos: ['ouro', 'primeiro', 'medalha'] },
      { char: '🥈', termos: ['prata', 'segundo', 'medalha'] },
      { char: '🥉', termos: ['bronze', 'terceiro', 'medalha'] },
      { char: '🏅', termos: ['medalha', 'premio'] },
      { char: '🎯', termos: ['alvo', 'certeiro', 'meta'] },
      { char: '🎮', termos: ['videogame', 'jogo', 'controle', 'game'] },
      { char: '🕹️', termos: ['joystick', 'arcade', 'jogo'] },
      { char: '🎲', termos: ['dado', 'sorte', 'jogo'] },
      { char: '♟️', termos: ['xadrez', 'peao'] },
      { char: '🎰', termos: ['caca', 'niquel', 'sorte'] },
      { char: '🎳', termos: ['boliche'] },
      { char: '🎨', termos: ['arte', 'pintura', 'paleta'] },
      { char: '🎭', termos: ['teatro', 'mascaras'] },
      { char: '🎤', termos: ['microfone', 'cantar', 'karaoke'] },
      { char: '🎧', termos: ['fone', 'ouvido', 'musica'] },
      { char: '🎵', termos: ['musica', 'nota'] },
      { char: '🎶', termos: ['musica', 'notas'] },
      { char: '🎸', termos: ['guitarra', 'violao'] },
      { char: '🥁', termos: ['bateria', 'tambor'] },
      { char: '🎹', termos: ['piano', 'teclado'] },
      { char: '🎺', termos: ['trompete'] },
      { char: '🎷', termos: ['saxofone'] },
      { char: '🎬', termos: ['cinema', 'claquete', 'filme'] },
      { char: '🎪', termos: ['circo', 'tenda'] },
      { char: '🎉', termos: ['festa', 'comemoracao', 'parabens'] },
      { char: '🎊', termos: ['confete', 'festa'] },
      { char: '🎈', termos: ['balao', 'festa'] },
      { char: '🎁', termos: ['presente', 'agrado'] },
      { char: '🎇', termos: ['fogos', 'artificio'] },
      { char: '🎆', termos: ['fogos', 'artificio', 'ano', 'novo'] },
    ],
  },
  {
    id: 'lugares',
    nome: 'Viagens e lugares',
    icone: '🚗',
    emojis: [
      { char: '🚗', termos: ['carro', 'automovel'] },
      { char: '🚕', termos: ['taxi'] },
      { char: '🚙', termos: ['suv', 'carro'] },
      { char: '🚌', termos: ['onibus'] },
      { char: '🚎', termos: ['trolebus', 'onibus'] },
      { char: '🏎️', termos: ['corrida', 'formula', 'carro'] },
      { char: '🚓', termos: ['policia', 'viatura'] },
      { char: '🚑', termos: ['ambulancia'] },
      { char: '🚒', termos: ['bombeiro', 'caminhao'] },
      { char: '🚚', termos: ['caminhao', 'entrega'] },
      { char: '🚜', termos: ['trator'] },
      { char: '🛵', termos: ['scooter', 'moto'] },
      { char: '🏍️', termos: ['moto', 'motocicleta'] },
      { char: '🚲', termos: ['bicicleta', 'bike'] },
      { char: '🛴', termos: ['patinete'] },
      { char: '🚄', termos: ['trem', 'bala', 'rapido'] },
      { char: '🚆', termos: ['trem'] },
      { char: '🚇', termos: ['metro'] },
      { char: '✈️', termos: ['aviao', 'voo', 'viagem'] },
      { char: '🚀', termos: ['foguete', 'lancamento', 'rapido', 'espaco'] },
      { char: '🛸', termos: ['disco', 'voador', 'ovni'] },
      { char: '🚁', termos: ['helicoptero'] },
      { char: '⛵', termos: ['veleiro', 'barco'] },
      { char: '🚢', termos: ['navio'] },
      { char: '⚓', termos: ['ancora'] },
      { char: '🗺️', termos: ['mapa', 'viagem'] },
      { char: '🧭', termos: ['bussola', 'direcao'] },
      { char: '🏔️', termos: ['montanha', 'neve'] },
      { char: '🏕️', termos: ['acampamento', 'barraca'] },
      { char: '🏖️', termos: ['praia', 'guarda', 'sol'] },
      { char: '🏝️', termos: ['ilha', 'deserta'] },
      { char: '🌋', termos: ['vulcao'] },
      { char: '🏠', termos: ['casa', 'lar'] },
      { char: '🏡', termos: ['casa', 'jardim'] },
      { char: '🏢', termos: ['predio', 'escritorio', 'empresa'] },
      { char: '🏥', termos: ['hospital'] },
      { char: '🏦', termos: ['banco'] },
      { char: '🏨', termos: ['hotel'] },
      { char: '🏫', termos: ['escola'] },
      { char: '⛪', termos: ['igreja'] },
      { char: '🏟️', termos: ['estadio', 'arena'] },
      { char: '🗼', termos: ['torre'] },
      { char: '🗽', termos: ['estatua', 'liberdade'] },
      { char: '🌆', termos: ['cidade', 'entardecer'] },
      { char: '🌃', termos: ['cidade', 'noite'] },
      { char: '🌉', termos: ['ponte', 'noite'] },
      { char: '🚦', termos: ['semaforo', 'transito'] },
      { char: '🗿', termos: ['moai', 'estatua'] },
    ],
  },
  {
    id: 'objetos',
    nome: 'Objetos',
    icone: '💻',
    emojis: [
      { char: '💻', termos: ['notebook', 'computador', 'laptop', 'trabalho'] },
      { char: '🖥️', termos: ['computador', 'monitor', 'desktop'] },
      { char: '⌨️', termos: ['teclado'] },
      { char: '🖱️', termos: ['mouse'] },
      { char: '🖨️', termos: ['impressora'] },
      { char: '📱', termos: ['celular', 'telefone', 'smartphone'] },
      { char: '☎️', termos: ['telefone', 'fixo'] },
      { char: '📞', termos: ['telefone', 'ligacao', 'chamada'] },
      { char: '📷', termos: ['camera', 'foto'] },
      { char: '📹', termos: ['filmadora', 'video'] },
      { char: '🎥', termos: ['camera', 'cinema', 'filme'] },
      { char: '📺', termos: ['televisao', 'tv'] },
      { char: '📻', termos: ['radio'] },
      { char: '🔋', termos: ['bateria', 'pilha'] },
      { char: '🔌', termos: ['tomada', 'plugue', 'energia'] },
      { char: '💡', termos: ['lampada', 'ideia', 'luz'] },
      { char: '🔦', termos: ['lanterna'] },
      { char: '🕯️', termos: ['vela'] },
      { char: '💰', termos: ['dinheiro', 'saco', 'grana'] },
      { char: '💵', termos: ['dinheiro', 'nota', 'dolar'] },
      { char: '💳', termos: ['cartao', 'credito', 'pagamento'] },
      { char: '💎', termos: ['diamante', 'joia', 'valioso'] },
      { char: '⚖️', termos: ['balanca', 'justica', 'equilibrio'] },
      { char: '🔧', termos: ['chave', 'inglesa', 'ferramenta', 'conserto'] },
      { char: '🔨', termos: ['martelo', 'ferramenta'] },
      { char: '🛠️', termos: ['ferramentas', 'conserto', 'manutencao'] },
      { char: '⚙️', termos: ['engrenagem', 'configuracao', 'ajuste'] },
      { char: '🧰', termos: ['caixa', 'ferramentas'] },
      { char: '🧲', termos: ['ima', 'magnetico'] },
      { char: '🔒', termos: ['cadeado', 'fechado', 'seguranca'] },
      { char: '🔓', termos: ['cadeado', 'aberto', 'destravado'] },
      { char: '🔑', termos: ['chave', 'acesso'] },
      { char: '🗝️', termos: ['chave', 'antiga'] },
      { char: '🚪', termos: ['porta'] },
      { char: '🪑', termos: ['cadeira'] },
      { char: '🛏️', termos: ['cama', 'dormir'] },
      { char: '🚿', termos: ['chuveiro', 'banho'] },
      { char: '🧴', termos: ['frasco', 'sabonete'] },
      { char: '🧹', termos: ['vassoura', 'limpeza'] },
      { char: '🧼', termos: ['sabao', 'limpeza'] },
      { char: '📦', termos: ['caixa', 'pacote', 'entrega'] },
      { char: '📫', termos: ['caixa', 'correio', 'carta'] },
      { char: '✉️', termos: ['carta', 'email', 'envelope'] },
      { char: '📝', termos: ['nota', 'anotacao', 'escrever'] },
      { char: '📄', termos: ['documento', 'papel', 'arquivo'] },
      { char: '📊', termos: ['grafico', 'barras', 'dados', 'relatorio'] },
      { char: '📈', termos: ['grafico', 'subindo', 'crescimento', 'alta'] },
      { char: '📉', termos: ['grafico', 'caindo', 'queda', 'baixa'] },
      { char: '📅', termos: ['calendario', 'data'] },
      { char: '📌', termos: ['alfinete', 'fixar', 'marcar'] },
      { char: '📎', termos: ['clipe', 'anexo'] },
      { char: '✂️', termos: ['tesoura', 'cortar'] },
      { char: '📚', termos: ['livros', 'estudo', 'leitura'] },
      { char: '📖', termos: ['livro', 'aberto', 'ler'] },
      { char: '🔍', termos: ['lupa', 'buscar', 'procurar', 'pesquisa'] },
      { char: '🔬', termos: ['microscopio', 'ciencia'] },
      { char: '🔭', termos: ['telescopio', 'observar'] },
      { char: '💊', termos: ['remedio', 'comprimido', 'pilula'] },
      { char: '💉', termos: ['injecao', 'vacina', 'seringa'] },
      { char: '🩹', termos: ['curativo', 'bandeide'] },
      { char: '🧪', termos: ['tubo', 'ensaio', 'experimento'] },
      { char: '🎓', termos: ['formatura', 'diploma', 'faculdade'] },
      { char: '👑', termos: ['coroa', 'rei', 'rainha'] },
      { char: '👕', termos: ['camiseta', 'roupa'] },
      { char: '👖', termos: ['calca', 'jeans'] },
      { char: '👟', termos: ['tenis', 'sapato', 'calcado'] },
      { char: '🧢', termos: ['bone', 'chapeu'] },
      { char: '🕶️', termos: ['oculos', 'sol'] },
      { char: '👓', termos: ['oculos', 'grau'] },
      { char: '🎒', termos: ['mochila'] },
      { char: '💼', termos: ['maleta', 'trabalho', 'negocio'] },
      { char: '⌚', termos: ['relogio', 'pulso'] },
      { char: '⏰', termos: ['despertador', 'alarme', 'hora'] },
      { char: '⏳', termos: ['ampulheta', 'tempo', 'esperando'] },
      { char: '🔔', termos: ['sino', 'notificacao', 'aviso'] },
      { char: '🔕', termos: ['sino', 'mudo', 'silenciado'] },
      { char: '📢', termos: ['megafone', 'anuncio', 'aviso'] },
      { char: '🎙️', termos: ['microfone', 'estudio', 'podcast'] },
      { char: '🛒', termos: ['carrinho', 'compras'] },
      { char: '🗑️', termos: ['lixeira', 'lixo', 'excluir'] },
    ],
  },
  {
    id: 'simbolos',
    nome: 'Símbolos',
    icone: '❤️',
    emojis: [
      { char: '❤️', termos: ['coracao', 'amor', 'vermelho'] },
      { char: '🧡', termos: ['coracao', 'laranja'] },
      { char: '💛', termos: ['coracao', 'amarelo'] },
      { char: '💚', termos: ['coracao', 'verde'] },
      { char: '💙', termos: ['coracao', 'azul'] },
      { char: '💜', termos: ['coracao', 'roxo'] },
      { char: '🖤', termos: ['coracao', 'preto'] },
      { char: '🤍', termos: ['coracao', 'branco'] },
      { char: '🤎', termos: ['coracao', 'marrom'] },
      { char: '💔', termos: ['coracao', 'partido', 'triste'] },
      { char: '❣️', termos: ['coracao', 'exclamacao'] },
      { char: '💕', termos: ['coracoes', 'amor'] },
      { char: '💞', termos: ['coracoes', 'girando'] },
      { char: '💓', termos: ['coracao', 'batendo'] },
      { char: '💗', termos: ['coracao', 'crescendo'] },
      { char: '💖', termos: ['coracao', 'brilhando'] },
      { char: '💘', termos: ['coracao', 'flecha', 'paixao'] },
      { char: '💝', termos: ['coracao', 'presente', 'laco'] },
      { char: '✅', termos: ['certo', 'ok', 'feito', 'confirmado'] },
      { char: '☑️', termos: ['marcado', 'caixa', 'feito'] },
      { char: '✔️', termos: ['certo', 'confirmado'] },
      { char: '❌', termos: ['errado', 'nao', 'x', 'cancelar'] },
      { char: '❎', termos: ['errado', 'x'] },
      { char: '⭕', termos: ['circulo', 'certo'] },
      { char: '🚫', termos: ['proibido', 'bloqueado', 'nao'] },
      { char: '⛔', termos: ['proibido', 'entrada'] },
      { char: '⚠️', termos: ['atencao', 'aviso', 'cuidado', 'perigo'] },
      { char: '❗', termos: ['exclamacao', 'atencao', 'importante'] },
      { char: '❓', termos: ['interrogacao', 'duvida', 'pergunta'] },
      { char: '💯', termos: ['cem', 'nota', 'perfeito', 'total'] },
      { char: '🔴', termos: ['circulo', 'vermelho', 'gravando'] },
      { char: '🟠', termos: ['circulo', 'laranja'] },
      { char: '🟡', termos: ['circulo', 'amarelo'] },
      { char: '🟢', termos: ['circulo', 'verde', 'online'] },
      { char: '🔵', termos: ['circulo', 'azul'] },
      { char: '🟣', termos: ['circulo', 'roxo'] },
      { char: '⚫', termos: ['circulo', 'preto'] },
      { char: '⚪', termos: ['circulo', 'branco'] },
      { char: '🔺', termos: ['triangulo', 'vermelho', 'cima'] },
      { char: '🔻', termos: ['triangulo', 'vermelho', 'baixo'] },
      { char: '🔶', termos: ['losango', 'laranja'] },
      { char: '⬆️', termos: ['seta', 'cima'] },
      { char: '⬇️', termos: ['seta', 'baixo'] },
      { char: '⬅️', termos: ['seta', 'esquerda'] },
      { char: '➡️', termos: ['seta', 'direita'] },
      { char: '🔄', termos: ['recarregar', 'atualizar', 'ciclo'] },
      { char: '🔙', termos: ['voltar', 'back'] },
      { char: '▶️', termos: ['play', 'tocar', 'iniciar'] },
      { char: '⏸️', termos: ['pausa', 'pausar'] },
      { char: '⏹️', termos: ['parar', 'stop'] },
      { char: '⏭️', termos: ['proximo', 'pular'] },
      { char: '🔀', termos: ['aleatorio', 'embaralhar'] },
      { char: '🔁', termos: ['repetir', 'loop'] },
      { char: '➕', termos: ['mais', 'adicionar', 'soma'] },
      { char: '➖', termos: ['menos', 'remover', 'subtrair'] },
      { char: '✖️', termos: ['vezes', 'multiplicar'] },
      { char: '➗', termos: ['dividir', 'divisao'] },
      { char: '♾️', termos: ['infinito'] },
      { char: '💤', termos: ['dormindo', 'sono', 'zzz'] },
      { char: '💬', termos: ['balao', 'conversa', 'mensagem', 'chat'] },
      { char: '💭', termos: ['balao', 'pensamento', 'pensando'] },
      { char: '🗯️', termos: ['balao', 'raiva', 'grito'] },
      { char: '🔞', termos: ['dezoito', 'adulto', 'proibido'] },
      { char: '♻️', termos: ['reciclar', 'reciclagem'] },
      { char: '⚜️', termos: ['flor', 'lis'] },
      { char: '🔱', termos: ['tridente'] },
      { char: '⭐', termos: ['estrela', 'favorito'] },
      { char: '🌐', termos: ['globo', 'internet', 'web', 'mundo'] },
      { char: '♠️', termos: ['espadas', 'carta', 'baralho'] },
      { char: '♥️', termos: ['copas', 'carta', 'baralho'] },
      { char: '♦️', termos: ['ouros', 'carta', 'baralho'] },
      { char: '♣️', termos: ['paus', 'carta', 'baralho'] },
    ],
  },
  {
    id: 'bandeiras',
    nome: 'Bandeiras',
    icone: '🏳️',
    emojis: [
      { char: '🏳️', termos: ['bandeira', 'branca', 'rendicao'] },
      { char: '🏴', termos: ['bandeira', 'preta'] },
      { char: '🏁', termos: ['bandeira', 'quadriculada', 'corrida', 'chegada'] },
      { char: '🚩', termos: ['bandeira', 'marcacao', 'alerta'] },
      { char: '🏳️‍🌈', termos: ['bandeira', 'arco', 'iris', 'orgulho', 'lgbt'] },
      { char: '🇧🇷', termos: ['brasil', 'brasileira', 'bandeira'] },
      { char: '🇵🇹', termos: ['portugal', 'portuguesa', 'bandeira'] },
      { char: '🇺🇸', termos: ['estados', 'unidos', 'eua', 'americana'] },
      { char: '🇦🇷', termos: ['argentina', 'bandeira'] },
      { char: '🇪🇸', termos: ['espanha', 'espanhola', 'bandeira'] },
      { char: '🇫🇷', termos: ['franca', 'francesa', 'bandeira'] },
      { char: '🇮🇹', termos: ['italia', 'italiana', 'bandeira'] },
      { char: '🇩🇪', termos: ['alemanha', 'alema', 'bandeira'] },
      { char: '🇬🇧', termos: ['reino', 'unido', 'inglaterra', 'bandeira'] },
      { char: '🇯🇵', termos: ['japao', 'japonesa', 'bandeira'] },
      { char: '🇨🇳', termos: ['china', 'chinesa', 'bandeira'] },
      { char: '🇰🇷', termos: ['coreia', 'sul', 'bandeira'] },
      { char: '🇨🇦', termos: ['canada', 'bandeira'] },
      { char: '🇲🇽', termos: ['mexico', 'bandeira'] },
      { char: '🇨🇱', termos: ['chile', 'bandeira'] },
      { char: '🇨🇴', termos: ['colombia', 'bandeira'] },
      { char: '🇺🇾', termos: ['uruguai', 'bandeira'] },
      { char: '🇵🇾', termos: ['paraguai', 'bandeira'] },
      { char: '🇦🇺', termos: ['australia', 'bandeira'] },
    ],
  },
]

/** Remove acentos para a busca casar com o que a pessoa digitou. */
function semAcento(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
}

export interface EmojiEncontrado {
  char: string
  termos: string[]
}

/**
 * Procura emojis pelo termo digitado.
 *
 * A ordem privilegia quem começa com o termo: quem digita "cor" quer ver coração
 * antes de "tricolor". Sem isso, uma busca curta devolve resultados que parecem
 * aleatórios.
 */
export function buscarEmojis(termo: string, limite = 60): EmojiEncontrado[] {
  const alvo = semAcento(termo.trim())
  if (!alvo) return []

  const comecam: EmojiEncontrado[] = []
  const contem: EmojiEncontrado[] = []

  for (const categoria of CATEGORIAS_DE_EMOJI) {
    for (const emoji of categoria.emojis) {
      let posicao = -1
      for (const palavra of emoji.termos) {
        const indice = semAcento(palavra).indexOf(alvo)
        if (indice === 0) {
          posicao = 0
          break
        }
        if (indice > 0 && posicao !== 0) posicao = indice
      }

      if (posicao === 0) comecam.push(emoji)
      else if (posicao > 0) contem.push(emoji)

      if (comecam.length >= limite) return comecam.slice(0, limite)
    }
  }

  return [...comecam, ...contem].slice(0, limite)
}

/** Todos os emojis num único vetor, para busca por caractere. */
export const TODOS_OS_EMOJIS = CATEGORIAS_DE_EMOJI.flatMap((c) => c.emojis)
